import React, {useEffect, useState, useCallback} from "react";
import {LocationContext, LocationContextProps} from "./location-context-props.ts";
import useFilters from "../../../hooks/use-filters.ts";

const LocationContextProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const { isLoading, error, data } = useFilters();
    
    // Tableau des localisations sélectionnées.
    const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
    
    // Gestion des sélections de filtre par niveau. Ex : [null, X]. Permet de déterminer l'affichage des menus déroulants.
    const [filterDataLocations, setFilterDataLocations] = useState<(number | null)[]>([null]);

    // Gestion de la présélection initiale
    useEffect(() => {
        if (data && data[0]?.localisations.length > 0) {
            const firstId = data[0].localisations[0].id;
            setSelectedLocations([firstId]);
            setFilterDataLocations([firstId]);
        }
    }, [data])
    
    /**
     * Retrieves a list of selected IDs, including the initially selected item and all of its descendant items across hierarchical levels.
     *
     * The function first determines the most recently selected ID from the `selectedIds` array. It then identifies
     * the corresponding data entry in the `data` structure and retrieves its children recursively across different hierarchical levels.
     * Each child is identified by matching its `parentId` with the IDs of the current set of selected children.
     *
     * The result is an array containing the ID of the initially selected item and the IDs of all its descendants.
     *
     * Dependencies:
     * - `data` should be an array of hierarchical levels, each containing a list of localisations.
     * - `selectedIds` should be an array of IDs representing selected items at each level in the hierarchy.
     *
     * Returns:
     * - An array of IDs, including the selected ID and IDs of all its child localisations.
     *
     * Note:
     * - Returns an empty array if no data or selected ID is found.
     * - Uses the `findLastIndex` method to determine the most recent selection.
     * - The operation relies on the structure of `data` and assumes the presence of `localisations` and `parentId` properties in each level.
     */
    const getSelectedAndChildrenIds = useCallback(() => {
        if (!data) return [];

        const lastSelectedIndex = filterDataLocations.findLastIndex(id => id != null);
        if (lastSelectedIndex === -1) return [];

        const selectedId = filterDataLocations[lastSelectedIndex];
        if (selectedId == null) return [];

        const selectedLevel = data[lastSelectedIndex];
        const selected = selectedLevel.localisations.find(l => l.id === selectedId);
        if (!selected) return [];

        // Obtention de tous les niveaux enfants.
        return data
            .slice(lastSelectedIndex + 1)
            .reduce(({ids, currentIds}, nextLevel) => {
                const childLocalisations = nextLevel.localisations.filter(l => currentIds.includes(l.parentId));
                const childrenIds = childLocalisations.map(c => c.id);

                return {
                    ids: [...ids, ...childrenIds],
                    currentIds: childrenIds
                };
            }, { ids: [selected.id], currentIds: [selected.id] })
            .ids;
    }, [data, filterDataLocations]);

    // Gestion de la "sélection" des filtres parents.
    useEffect(() => {
        if (!data) return;

        const newSelected = [...filterDataLocations];

        const reversedIndexes = Array.from({ length: filterDataLocations.length - 1 }, (_, i) => filterDataLocations.length - 1 - i);

        reversedIndexes.forEach(i => {
            if (i === 0) return;

            const selectedId = filterDataLocations[i];
            if (selectedId == null) return;

            const currentLevel = data[i];
            const parentLevel = data[i - 1];
            if (!currentLevel || !parentLevel) return;

            const current = currentLevel.localisations.find(l => l.id === selectedId);
            if (!current) return;

            const parent = parentLevel.localisations.find(l => l.id === current.parentId);
            if (parent && newSelected[i - 1] !== parent.id) {
                newSelected[i - 1] = parent.id;
            }
        });

        if (JSON.stringify(newSelected) !== JSON.stringify(filterDataLocations)) {
            setFilterDataLocations(newSelected);
        }

        setSelectedLocations(getSelectedAndChildrenIds());
    }, [data, filterDataLocations, getSelectedAndChildrenIds, setFilterDataLocations, setSelectedLocations]);
    
    const contextProps: LocationContextProps = {
        isLoading,
        error,
        data,
        
        selectedLocations,
        setSelectedLocations,
        
        filterDataLocations,
        setFilterDataLocations,
    }
    
    return (
        <LocationContext.Provider value={contextProps}>
            {children}
        </LocationContext.Provider>
    )
}

export default LocationContextProvider;